import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Trash2, ShieldOff } from "lucide-react";

interface Row {
  email: string; reason: string; source: string; notes: string | null;
  first_seen_at: string; updated_at: string;
}

const PAGE_SIZE = 50;

const REASON_LABEL: Record<string, string> = {
  unsubscribed: "Unsubscribed",
  hard_bounce: "Hard bounce",
  complaint: "Spam complaint",
  invalid_email: "Invalid email",
  manual: "Manually added",
};

const REASON_COLOR: Record<string, string> = {
  unsubscribed: "bg-blue-100 text-blue-700",
  hard_bounce: "bg-amber-100 text-amber-700",
  complaint: "bg-red-100 text-red-700",
  invalid_email: "bg-gray-100 text-gray-700",
  manual: "bg-purple-100 text-purple-700",
};

export default function SuppressionPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  function changeSearch(v: string) { setSearch(v); setPage(0); }

  const { data, isLoading } = useQuery({
    queryKey: ["suppression", search, page],
    queryFn: () => api<{ rows: Row[]; total: number; byReason: Record<string, number> }>(
      `/api/admin/suppression?q=${encodeURIComponent(search)}&limit=${PAGE_SIZE}&offset=${page * PAGE_SIZE}`,
    ),
  });

  const remove = useMutation({
    mutationFn: (email: string) => api("/api/admin/suppression", { method: "DELETE", body: JSON.stringify({ email }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["suppression"] }),
  });

  const total = data?.total ?? 0;
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);
  const byReason = data?.byReason ?? {};

  return (
    <div className="p-4 md:p-8 max-w-5xl">
      <h1 className="text-2xl font-semibold text-gray-900 mb-1 flex items-center gap-2"><ShieldOff size={22} /> Suppression list</h1>
      <p className="text-sm text-gray-500 mb-6">
        Workspace-wide. Anyone on this list will be skipped on every send, by every user, until they're removed.
        Auto-populated by delivery events (hard bounce / spam complaint / invalid email) and unsubscribe clicks.
      </p>

      {/* Reason summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {Object.entries(REASON_LABEL).map(([k, label]) => (
          <div key={k} className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="text-xs text-gray-500">{label}</div>
            <div className="text-xl font-semibold text-gray-900 mt-1">{byReason[k] ?? 0}</div>
          </div>
        ))}
      </div>

      <input type="search" placeholder="Search email…" value={search} onChange={(e) => changeSearch(e.target.value)}
        className="w-full max-w-sm border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4 focus:outline-none focus:border-indigo-500" />

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="text-left px-5 py-2">Email</th>
              <th className="text-left px-5 py-2">Reason</th>
              <th className="text-left px-5 py-2">Source</th>
              <th className="text-left px-5 py-2">First seen</th>
              <th className="px-5 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {(data?.rows ?? []).map((r) => (
              <tr key={r.email} className="border-t border-gray-100">
                <td className="px-5 py-2 font-mono text-xs">{r.email}</td>
                <td className="px-5 py-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${REASON_COLOR[r.reason] ?? "bg-gray-100 text-gray-700"}`}>
                    {REASON_LABEL[r.reason] ?? r.reason}
                  </span>
                </td>
                <td className="px-5 py-2 text-xs text-gray-500" title={r.notes ?? undefined}>{r.source}</td>
                <td className="px-5 py-2 text-xs text-gray-500">{new Date(r.first_seen_at).toLocaleString()}</td>
                <td className="px-5 py-2 text-right">
                  <button
                    onClick={() => { if (confirm(`Remove ${r.email} from suppression list?\n\nThey'll be eligible to receive emails again. If they unsubscribe a second time, they'll be re-suppressed.`)) remove.mutate(r.email); }}
                    disabled={remove.isPending}
                    className="text-xs text-red-600 hover:text-red-800 inline-flex items-center gap-1 disabled:opacity-50">
                    <Trash2 size={12} /> Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!isLoading && data?.rows.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-8">No entries{search ? " match this search" : " yet"}.</p>
        )}

        {total > PAGE_SIZE && (
          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
            <div>{page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}</div>
            <div className="flex gap-2">
              <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
                className="px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50">Prev</button>
              <button onClick={() => setPage((p) => Math.min(lastPage, p + 1))} disabled={page >= lastPage}
                className="px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
